/**
 * 공통 Zod 필드 스키마
 *
 * 폼 스키마를 조합할 때 필드 단위로 픽해서 사용한다.
 *
 * @example
 * import { z } from "zod";
 * import { phoneField, bizNumField, passwordField } from "@linkup/validators";
 *
 * const memberSchema = z.object({
 *   name:     z.string().min(1, "이름을 입력해주세요."),
 *   phone:    phoneField,
 *   bizNo:    bizNumField.optional(),
 *   password: passwordField,
 * });
 */

import { z } from "zod";

// ─── 문자열 기본 ──────────────────────────────────────────────────────────────

/** 필수 문자열 */
export const requiredStringField = z.string().min(1, "필수 항목입니다.");

/** 선택 문자열 (빈 문자열 허용) */
export const optionalStringField = z.string().optional().or(z.literal(""));

// ─── 연락처 ───────────────────────────────────────────────────────────────────

/**
 * 전화번호 — 숫자 10~11자리 (하이픈 제외)
 * AdminFormField type="tel" 과 함께 사용하면 자동 포맷팅됨
 */
export const phoneField = z
    .string()
    .regex(/^\d{10,11}$/, "올바른 전화번호를 입력해주세요. (숫자 10~11자리)");

/**
 * 사업자번호 — 숫자 10자리 (하이픈 제외)
 * AdminFormField type="bizNum" 과 함께 사용하면 자동 포맷팅됨
 */
export const bizNumField = z
    .string()
    .regex(/^\d{10}$/, "올바른 사업자번호를 입력해주세요. (숫자 10자리)");

// ─── 이메일 ───────────────────────────────────────────────────────────────────

export const emailField = z
    .string()
    .min(1, "이메일을 입력해주세요.")
    .email("올바른 이메일 형식이 아닙니다.");

// ─── 비밀번호 ─────────────────────────────────────────────────────────────────

/**
 * 비밀번호 — 8자 이상, 영문 + 숫자 포함
 */
export const passwordField = z
    .string()
    .min(8, "비밀번호는 8자 이상이어야 합니다.")
    .refine((v) => /[a-zA-Z]/.test(v), "영문을 포함해야 합니다.")
    .refine((v) => /\d/.test(v), "숫자를 포함해야 합니다.");

// ─── 날짜 ────────────────────────────────────────────────────────────────────

/**
 * 날짜 — YYYY-MM-DD 형식
 * AdminDatePicker / AdminDateInput 의 value 타입과 일치
 */
export const dateField = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)");

/** 선택 날짜 (빈 문자열 허용) */
export const optionalDateField = z
    .string()
    .refine(
        (v) => v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v),
        "날짜 형식이 올바르지 않습니다. (YYYY-MM-DD)"
    );

// ─── 숫자 ────────────────────────────────────────────────────────────────────

/**
 * 콤마 포맷 숫자 문자열 (예: "1,234")
 * AdminFormField type="comma" 와 함께 사용
 */
export const commaNumberField = z
    .string()
    .refine(
        (v) => v === "" || /^[\d,]+$/.test(v),
        "숫자만 입력 가능합니다."
    );

/** 양의 정수 */
export const positiveIntField = z
    .number({ error: "숫자를 입력해주세요." })
    .int("정수만 입력 가능합니다.")
    .positive("0보다 큰 숫자를 입력해주세요.");

// ─── URL ─────────────────────────────────────────────────────────────────────

export const urlField = z
    .string()
    .url("올바른 URL 형식이 아닙니다.")
    .optional()
    .or(z.literal(""));
